import { gql } from "@apollo/client";

import graphql from "./gql";

export const GET_AUTH_TOKEN = gql`
  query GetAuthToken ($idToken: String!, $rememberMe: Boolean) {
    getAuthToken(idToken: $idToken, rememberMe: $rememberMe) {
      authToken
    }
  }
`;

export const GET_MEMBER = gql`
  query Member {
    member {
      memberUid
      firstname
      lastname
      birthday
      email
      address
      complement
      zip
      city
      country
      region
      lang
      profile {
        pseudo
        remainingTime
        memberCode
        weeklyOrdersLimit
        favorites
        fidelityPoints
        phoneUidForAlerts
        phoneUidLastUsed
        queueNotificationChannels
        createdAt
      }
      consents {
        purposeUid
        purposeKey
        type
        contentId
        required
        requiresConsent
        lastModifiedAt
        consentDate
        userUid
        value
        context
      }
      phoneNumbers {
        phoneUid
        phoneNumber
        country
        type
        status
        phoneCode
        servicePhones {
          calls
          sms
        }
      }
      consultedExperts
      alerts {
        alertUid
        expertUid
        expertChannels
        notificationChannels
        validTo
      }
    }
    activeSurveys {
      surveyUid
      title
      description
      language
      createdAt
      startDate
      endDate
      targetQuery
      singleResponse
      country
      status
      context
      userType
      advertised
      questions {
        questionUid
        title
        content
        required
        showQuestion
        showDescription
        type
        settings {
          allowOther
          options {
            key
            label
            action {
              type
              questionUid
            }
          }
          min
          max
          step
          minLabel
          midLabel
          maxLabel
          collectEmail
          button {
            text
            link
          }
        }
        order
      }
    }
    advisories {
      data {
        advisoryUid
        type
        createdAt
      }
      totalRows
    }
    announcements {
      announcementUid
      type
      typeData
      content
      notify
      startDate
      endDate
    }
    pendingActions {
      onGoingConsultation {
        expertUid
        channel
        channelRefUid
        maxDuration
        consultationUid
        createdAt
        startDate
      }
      trialOffer {
        trialOfferUid
        memberUid
        expertUid
        channels
      }
      trialConsultation {
        trialConsultationUid
        expertUid
        memberUid
        channel
        channelRefUid
        language
        trialUid
        status
        createdAt
        startDate
      }
    }
    notification {
      notifications {
        notificationUid
        ack
        notificationType
        metadata
        priority
        createdAt
      }
      unreadNotificationsCount
    }
    userQueues {
      queueItemUid
      queueItemType
      queueUid
      targetUid
      queuedAt
      dropAt
      metadata {
        servicePhone
        isEphemeral
        memberPhoneUid
      }
      items {
        position
        duration
        startedAt
        type
        metadata {
          isEphemeral
        }
      }
    }
  }
`;

export const GET_ADVISORIES = gql`
query Advisories {
  advisories {
    data {
      advisoryUid
      type
      targetType
      targetUid
      expiresAt
      createdAt
    }
    totalRows
  }
}`;

export const GET_PENDING_ACTIONS = gql`
  query PendingActions {
    pendingActions {
      onGoingConsultation {
        expertUid
        channel
        channelRefUid
        maxDuration
        consultationUid
        createdAt
        startDate
      }
      trialOffer {
        trialOfferUid
        memberUid
        expertUid
        channels
      }
      trialConsultation {
        trialConsultationUid
        expertUid
        memberUid
        channel
        channelRefUid
        language
        trialUid
        status
        createdAt
        startDate
      }
    }
  }
`;

export const GET_MEMBER_MINUTE_HISTORY = `
  query MinuteHistory($limit: Int!, $offset: Int!) {
    member {
      minuteHistory(limit: $limit, offset: $offset) {
        totalRows
        data {
          minuteHistoryUid
          from
          to
          type
          action
          createdAt
        }
      }
    }
  }
`;

// export const GET_EXPERTS = gql`
//   query Experts($profileLanguage: String!) {
//     experts(profileLanguage: $profileLanguage) {
//       expertUid
//       expertCode
//       picture
//       pseudo
//       spokenLanguages
//       vocalMessage
//       quote
//       presenceStatus {
//         phoneStatus
//         videoStatus
//         livechatStatus
//         writtenStatus
//       }
//       skills
//       totalConsultations
//       totalRatings
//     }
//   }
// `;

export const GET_EXPERT_CONTENT = gql`
  query Expert($expertUid: ID!, $profileLanguage: String!) {
    expert(expertUid: $expertUid, profileLanguage: $profileLanguage) {
      content {
        description
        writtenMessage
      }
    }
  }
`;

export const GET_EXPERTS_IDENTIFIERS = gql`
  query ExpertsIdentifiers($profileLanguage: String!) {
    expertsIdentifiers(profileLanguage: $profileLanguage) {
      expertUid
      pseudo
      expertCode
      picture
      online
      hidden
      status
      createdAt
    }
  }
`;

export const GET_EXPERTS_LIST = gql`
  query ExpertsList($expertFilters: ExpertListFilters!) {
    expertsList(expertFilters: $expertFilters)
  }
`;

export const GET_EXPERTS_DATA = gql`
  query ExpertsData($expertFilters: ExpertDataFilters!) {
    expertsData(expertFilters: $expertFilters) {
      expertUid
      expertCode
      picture
      pseudo
      spokenLanguages
      vocalMessage
      quote
      createdAt
      parameters {
        phoneEnabled
        videoEnabled
        livechatEnabled
        writtenEnabled
        writtenLimit
      }
      presenceStatus {
        phoneStatus
        videoStatus
        livechatStatus
        writtenStatus
        activityStatus
      }
      skills
      totalConsultations
      totalRatings
      queue {
        queueUid
        items {
          position
          duration
          startedAt
          type
          metadata {
            isEphemeral
            maxDuration
          }
        }
      }
      planning {
        ownerUid
        from
        to
        slots {
          start
          end
          channels
        }
        queueSlots {
          start
          end
          channels
        }
      }
    }
  }
`;

export const GET_DAILY_HOROSCOPE = graphql(`
  query DailyHoroscopes($locale: String!, $timezone: String) {
    dailyHoroscopes(locale: $locale, timezone: $timezone) {
      sign
      love
      money
      health
      quote
      work
      family
      socialLife
      luckyNumber
      main
      wink
    }
  }
`);

export const GET_CONTENT = gql`
  query ContentQuery($locale: String!, $key: String, $namespace: String!) {
    content(locale: $locale, key: $key, namespace: $namespace) {
      key
      namespace
      content
    }
  }
`;

export const GET_PACKS = gql`
  query Packs {
    packs {
      packUid
      name
      time
      prices {
        currency
        value
      }
      color
      recommended
      fidelityPoints
    }
  }
`;

export const GET_PROMOTIONS = gql`
  query Promotions {
    promotions {
      promoUid
      type
      packs
      value
      startDate
      endDate
    }
  }
`;

export const GET_GLOBAL = gql`
  query Global {
    global {
      boosts {
        boostUid
        channels
        value
        startDate
        endDate
      }
      promotions {
        promoUid
        type
        packs
        value
        startDate
        endDate
      }
      ipCountry
    }
  }
`;

export const GET_ANNOUNCEMENTS = gql`
query Announcements($announcementUid: String)  {
  announcements(announcementUid: $announcementUid) {
    announcementUid
    type
    typeData
    content
    notify
    startDate
    endDate
  }
}
`;

export const GET_PAYMENT_METHODS = gql`
query Payment($countryCode: String!) {
  paymentBlocking {
    createdAt
    updatedAt
    paymentBlockingUid
    ref
    status
    type
    metadata {
      orderRef
    }
  }
  paymentCards {
    card
    paymentMethodUid
    orders {
      orderUid
      date
    }
  }
  paymentMethods(countryCode: $countryCode) {
    blocked
    name
    paymentMethodUid
  }
}
`;

export const GET_PAYMENT_HOSTED_TOKENIZATION = gql`
query PaymentHostedTokenization($locale: String!, $paymentMethodUid: String!) {
  paymentHostedTokenization(locale: $locale, paymentMethodUid: $paymentMethodUid) {
    url
    tokens
  }
}
`;

export const GET_ORDER_BY_ID = gql`
  query Order($orderId: String!) {
    order(orderId: $orderId) {
      orderId
      pack {
        packUid
        name
      }
      promoUids
      netPrice
      currency
      time
      status
      createdAt
    }
  }
`;

export const REFRESH_ORDER_BY_ID = gql`
  query OrderRefresh($orderId: String!) {
    orderRefresh(orderId: $orderId) {
      orderId
      pack {
        packUid
        name
        fidelityPoints
      }
      paymentMethod
      promoUids
      netPrice
      currency
      time
      status
      createdAt
      firstPurchase
    }
  }
`;

export const GET_ORDER_LIST = gql`
  query Orders($limit: Int!, $offset: Int!) {
    orders(limit: $limit, offset: $offset) {
      totalRows
      data {
        orderId
        pack {
          packUid
          name
        }
        promoUids
        grossPrice
        netPrice
        currency
        time
        status
        createdAt
      }
    }
  }
`;

export const GET_CONSULTATION_LIST = gql`
  query Consultations($filters: ConsultationFilters, $limit: Int!, $offset: Int!) {
    consultations(filters: $filters, limit: $limit, offset: $offset) {
      totalRows
      data {
        consultationUid
        memberUid
        expertUid
        language
        duration
        channel
        channelRefUid
        status
        rating {
          ratingUid
          rate
          comment
          ratingAnswer {
            ratingAnswerUid
            ratingUid
            expertUid
            comment
            validationComment
            language
            status
            validationRules
            context
            createdAt
            updatedAt
            publishAt
          }
          postponed
          refused
          rejected
          moderation
          validationComment
          createdAt
        }
        startDate
        endDate
        createdAt
        purgeDate
        boost {
          boostUid
          value
        }
      }
    }
  }
`;

export const GET_CONSULTATION = gql`
  query Consultation($consultationUid: String!) {
    consultation(consultationUid: $consultationUid) {
      consultationUid
      memberUid
      expertUid
      language
      duration
      channel
      channelRefUid
      status
      rating {
        ratingUid
        rate
        comment
        ratingAnswer {
          ratingAnswerUid
          ratingUid
          expertUid
          comment
          validationComment
          language
          status
          validationRules
          context
          createdAt
          updatedAt
          publishAt
        }
        postponed
        refused
        rejected
        moderation
        validationComment
      }
      failReason
      startDate
      endDate
      createdAt
      purgeDate
      boost {
        boostUid
        value
      }
    }
  }
`;

export const GET_TRIAL_CONSULTATION_LIST = gql`
  query TrialConsultationList($filters: TrialConsultationFilters, $limit: Int!, $offset: Int!) {
    trialConsultations(filters: $filters, limit: $limit, offset: $offset) {
      totalRows
      data {
        trialConsultationUid
        channel
        duration
        feedback {
          globalQuality
          receptionQuality
          responseQuality
          listeningQuality
          recommandation
          comment
        }
        language
        expertUid
        memberUid
        status
        channelRefUid
        startDate
        endDate
        createdAt
      }
    }
  }
`;

export const GET_TRIAL_CONSULTATION = gql`
  query TrialConsultation($trialConsultationUid: String!) {
    trialConsultation(trialConsultationUid: $trialConsultationUid) {
      trialConsultationUid
      channel
      duration
      feedback {
        globalQuality
        receptionQuality
        responseQuality
        listeningQuality
        recommandation
        comment
      }
      language
      expertUid
      memberUid
      status
      channelRefUid
      startDate
      endDate
      createdAt
    }
  }
`;

export const GET_USER_QUEUES = gql`
  query UserQueues {
    userQueues {
      queueItemUid
      queueItemType
      queueUid
      targetUid
      queuedAt
      dropAt
      metadata {
        servicePhone
        isEphemeral
        memberPhoneUid
      }
      items {
        position
        duration
        startedAt
        type
        metadata {
          isEphemeral
        }
      }
    }
  }
`;

export const GET_RATING_LIST = gql`
  query Ratings($filters: RatingFilters) {
    ratings(filters: $filters) {
      totalRows
      data {
        ratingUid
        expertUid
        memberUid
        memberPseudo
        rate
        comment
        ratingAnswer {
          ratingAnswerUid
          ratingUid
          expertUid
          comment
          validationComment
          language
          status
          validationRules
          context
          createdAt
          updatedAt
          publishAt
        }
        createdAt
      }
    }
  }
`;

export const GET_RATING_SUMMARY_LIST = gql`
  query RatingsSummary($filters: RatingSummaryFilters!) {
    ratingsSummary(filters: $filters) {
      rate
      count
    }
  }
`;

export const GET_NOTIFICATIONS = gql`
  query Notifications($offset: Int, $limit: Int) {
    notification {
      notifications(offset: $offset, limit: $limit) {
        notificationUid
        ack
        notificationType
        metadata
        createdAt
      }
      unreadNotificationsCount
    }
  }
`;

export const IS_MESSAGE_ALLOWED = gql`
  query MessageAllowed($correspondent: String!) {
    messageAllowed(correspondent: $correspondent)
  }
`;

export const GET_MESSAGES_SUMMARY = gql`
  query MessagesSummary($correspondent: String) {
    messagesSummary(correspondent: $correspondent) {
      blocked
      blockedBy
      recipient
      unreadMsg
      lastMsgDate
    }
  }
`;

export const GET_MESSAGES_LIST = gql`
  query MessagesList($correspondent: String!, $limit: Int!, $offset: Int!) {
    messagesList(correspondent: $correspondent, limit: $limit, offset: $offset) {
      totalRows
      data {
        attachments
        content
        messageUid
        sentAt
        recipient
        sender
        refusalReason
        viewedAt
        deletedAt
      }
    }
  }
`;

export const GET_EXPERT_LIMITS = gql`
  query ExpertLimits($expertUid: String!) {
    expertLimits(expertUid: $expertUid) {
      availableMessageNbr
      consultationSecondCtr
    }
  }
`;

export const GET_IVR_CALL = gql`
  query IvrCalls($ivrCallUid: String) {
    ivrCalls(ivrCallUid: $ivrCallUid) {
      ivrCallUid
      memberCallUid
      memberPhone
      expertUid
      memberUid
    }
  }
`;

export const GET_WRITTEN = gql`
  query Written($writtenUid: ID!) {
    written(writtenUid: $writtenUid) {
      writtenUid
      memberUid
      expertUid
      subject
      message
      attachments
      answer
      status
      refusalReason
      refusalComment
      createdAt
      answeredAt
      expireAt
    }
  }
`;

export const GET_LIVECHAT_ROOM = gql`
  query LivechatRoom($livechatRoomUid: ID!) {
    livechatRoom(livechatRoomUid: $livechatRoomUid) {
      livechatRoomUid
      participants {
        name
        userUid
      }
      messages {
        origin
        ts
        payload
      }
      maxTime
      context
      createdAt
      startDate
      endDate
      status
    }
  }
`;

export const GET_VIDEO_ROOM = gql`
  query VideoRoom($videoRoomUid: ID!) {
    videoRoom(videoRoomUid: $videoRoomUid) {
      videoRoomUid
      maxTime
      context
      createdAt
      startDate
      endDate
      participants {
        name
        userUid
        online
        isMe
        connectionIds
      }
      sessionId
      token
      status
    }
  }
`;

export const GET_DISPLAY_PHONES = gql`
  query DisplayPhones($usages: [DisplayPhoneUsage]!, $languages: [String], $countries: [String]) {
    displayPhones(usages: $usages, languages: $languages, countries: $countries) {
      phoneNumber
      country
      usage
      communications {
        phoneUsageUid
        callPrice
        callType
        callDisclaimer
        discontinuing
        unavailable
      }
    }
  }
`;

export const GET_SUPPORT_TICKETS = gql`
query Tickets($filters: TicketFilters) {
  tickets(filters: $filters) {
    data {
      userEmail
      ticketUid
      subject
      status
      userFirstname
      userLastname
      userPseudo
      createdAt
    }
    totalRows
  }
}
`;

export const GET_SUPPORT_TICKET = gql`
query Ticket($ticketUid: String!) {
  ticket(ticketUid: $ticketUid) {
    createdAt
    status
    subject
    userFirstname
    userLastname
    userEmail
    userPseudo
    userUid
    userType
    exchanges {
      ticketExchangeUid
      content
      origin
      createdAt
      attachments
      userPseudo
    }
  }
}
`;

// #region Survey
export const GET_ACTIVE_SURVEYS = gql`
  query GetActiveSurveys {
    activeSurveys {
      surveyUid
      title
      description
      language
      createdAt
      startDate
      endDate
      targetQuery
      singleResponse
      country
      status
      context
      userType
      advertised
      questions {
        questionUid
        title
        content
        required
        showQuestion
        showDescription
        type
        settings {
          allowOther
          options {
            key
            label
            action {
              type
              questionUid
            }
          }
          min
          max
          step
          minLabel
          midLabel
          maxLabel
          collectEmail
          button {
            text
            link
          }
        }
        order
      }
    }
  }
`;
// #endregion

// #region Blogs

export const GET_ARTICLE = gql`
  query Article($articleUid: ID!) {
    article(articleUid: $articleUid) {
      articleUid
      authorUid
      categoryUids
      status
      publishAt
      coverImageUrl
      imageAlts {
        key
        value
      }
      coverImageUrls {
        key
        value
      }
      shortLinkSlug
      reserved
      postscript {
        newsletter
        signup
        app
      }
      context
      content {
        articleContentUid
        articleUid
        readingTime
        status
        title
        body
        summary
        tldr
        nextTitle
        nextBody
        nextSummary
        nextTldr
        language
      }
      author {
        name
        avatarUrl
        avatarUrls {
          key
          value
        }
        titleKey
      }
      categories {
        categoryUid
        titleKey
        color
        imageUrl
        imageUrls {
          key
          value
        }
      }
    }
  }
`;

export const GET_ARTICLE_BY_SLUG = gql`
  query ArticleBySlug($slug: String!, $language: String!) {
    article(slug: $slug, language: $language) {
      articleUid
      authorUid
      categoryUids
      status
      publishAt
      coverImageUrl
      imageAlts {
        key
        value
      }
      coverImageUrls {
        key
        value
      }
      shortLinkSlug
      reserved
      postscript {
        newsletter
        signup
        app
      }
      context
      content {
        articleContentUid
        articleUid
        readingTime
        status
        title
        body
        summary
        tldr
        nextTitle
        nextBody
        nextSummary
        nextTldr
        language
      }
      author {
        name
        avatarUrl
        avatarUrls {
          key
          value
        }
        titleKey
        titles {
          key
          value
        }
        slug
      }
      imageAlts {
        key
        value
      }
      categories {
        categoryUid
        titleKey
        titles {
          key
          value
        }
        imageAlts {
          key
          value
        }
        slugs {
          key
          value
        }
        marketingOffers {
          titles {
            key
            value
          }
          descriptions {
            key
            value
          }
          imageUrls {
            key
            value
          }
          imageAlts {
            key
            value
          }
        }
        color
        imageUrl
        imageUrls {
          key
          value
        }
      }
    }
  }
`;

export const GET_ARTICLE_SUMMARIES = gql`
  query ArticleSummaries($filters: ArticleSummarySearch!) {
    articleSummaries(filters: $filters) {
      totalRows
      data {
        articleUid
        categoryUids
        status
        publishAt
        coverImageUrl
        imageAlts {
          key
          value
        }
        coverImageUrls {
          key
          value
        }
        shortLinkSlug
        reserved
        content {
          status
          readingTime
          title
          summary
          slug
        }
        author {
          slug
        }
        categories {
          categoryUid
          titleKey
          titles {
            key
            value
          }
          slugs {
            key
            value
          }
          color
          imageUrl
          imageUrls {
            key
            value
          }
        }
      }
    }
  }
`;

export const GET_ARTICLES_CATEGORY_BREAKDOWN = gql`
  query ArticlesCategoryBreakdown($filters: ArticlesCategoryBreakdownSearch!) {
    articlesCategoryBreakdown(filters: $filters) {
      categoryUid
      titleKey
      articleCount
    }
  }
`;

export const GET_ARTICLE_CATEGORIES = gql`
  query ArticleCategories($filters: ArticleCategoriesSearch!) {
    articleCategories(filters: $filters) {
      categoryUid
      titleKey
      titles {
        key
        value
      }
      imageAlts {
        key
        value
      }
      marketingOffers {
        titles {
          key
          value
        }
        descriptions {
          key
          value
        }
        imageUrls {
          key
          value
        }
        imageAlts {
          key
          value
        }
      }
      slugs {
        key
        value
      }
      descriptions {
        key
        value
      }
      imageAlts {
        key
        value
      }
      color
      imageUrl
      imageUrls {
        key
        value
      }
    }
  }
`;
export const GET_ARTICLE_CATEGORY = gql`
  query ArticleCategory($categoryUid: ID, $titleKey: String, $slug: String, $language: String) {
    articleCategory(categoryUid: $categoryUid, titleKey: $titleKey, slug: $slug, language: $language) {
      categoryUid
      titleKey
      titles {
        key
        value
      }
      imageAlts {
        key
        value
      }
      marketingOffers {
        titles {
          key
          value
        }
        descriptions {
          key
          value
        }
        imageUrls {
          key
          value
        }
        imageAlts {
          key
          value
        }
      }
      descriptions {
        key
        value
      }
      slugs {
        key
        value
      }
      color
      imageUrl
      imageUrls {
        key
        value
      }
    }
  }
`;

export const GET_BLOG_AUTHOR = gql`
  query GetBlogAuthor($slug: String!) {
    author(slug: $slug) {
      authorUid
      name
      avatarUrl
      avatarUrls {
        key
        value
      }
      titleKey
      titles {
        key
        value
      }
      descriptions {
        key
        value
      }
      imageAlts {
        key
        value
      }
      context
      slug
    }
  }
`;

export const GET_BLOG_AUTHOR_OR_CATEGORY = gql`
  query GetBlogAuthorOrCategory($slug: String!, $language: String!) {
    author(slug: $slug) {
      authorUid
    }
    articleCategory(slug: $slug, language: $language) {
      categoryUid
    }
  }
`;
// #endregion
